/**
 * AcceptInvitationPage
 * =====================
 *
 * Landing page for partner-staff Clerk invitations on the unified
 * primary host (`app.tagada.io`). When an invited user clicks the
 * "Accept invitation" button in their email, Clerk validates the
 * ticket on its frontend API and redirects the browser here with two
 * relevant query params:
 *
 *   - `__clerk_ticket` — the signed JWT we must hand to `signUp.create`.
 *   - `redirect_url`   — where to send the user once the session is
 *                        live (typically the partner's branded
 *                        satellite, e.g. `dashboard.tagadapay.io/home`).
 *
 * Without this page, the ticket fell through the SPA's catch-all
 * rewrite to the bare CRM `/sign-in`, which has no idea what to do
 * with a ticket and asked the user to type their email — surfacing
 * a confusing "Couldn't find your account" error.
 *
 * Behaviour:
 *   - If the user is already signed in (e.g. they re-clicked an
 *     invite after completing it), bounce straight to `redirect_url`
 *     so they don't see a flicker of sign-up UI.
 *   - Otherwise, consume the ticket via
 *     `signUp.create({ strategy: 'ticket', ticket })`. Clerk pre-fills
 *     the email + verifies it from the JWT, so most flows resolve to
 *     `status === 'complete'` immediately, at which point we activate
 *     the session and redirect cross-origin.
 *   - If Clerk reports `missing_requirements` (rare — only when the
 *     instance requires extra fields we can't derive from the ticket,
 *     e.g. a name), fall back to the existing `<CustomSignUp>` flow so
 *     the user can finish the form. The ticket stays in the URL and
 *     Clerk picks it up on the next `signUp.create` call.
 *   - All errors render an inline message with a "Request a new
 *     invitation" CTA so the user has a clear escape hatch instead of
 *     a stale "Couldn't find your account" loop.
 */
import { useEffect, useState } from 'react';
import { useSignUp, useSignIn, useAuth, useUser } from '@clerk/clerk-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomSignUp } from '@/components/auth/custom-sign-up';
import { BrandLogo } from '@/components/branding/brand-logo';
import { brandingConfig } from '@/config/branding';
import { useResolveSignInBrand } from '@/hooks/use-resolve-sign-in-brand';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { setGaIdentityFromSignup, trackGaSignUp } from '@/lib/analytics/ga';

type Phase =
  | { kind: 'loading' }
  | { kind: 'completing' }
  | { kind: 'fallback' }
  | { kind: 'error'; message: string };

function safeRedirect(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

export default function AcceptInvitationPage() {
  const { t } = useTranslation('auth');
  const { signUp, setActive, isLoaded } = useSignUp();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get('__clerk_ticket');
  const redirectUrl = safeRedirect(searchParams.get('redirect_url'));
  const brand = useResolveSignInBrand(redirectUrl);
  useBrandTheme(brand);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    if (!authLoaded || !isLoaded || !signInLoaded) return;

    const leave = () => {
      if (redirectUrl) {
        window.location.replace(redirectUrl);
      } else {
        navigate('/');
      }
    };

    // Already signed in (re-clicked link, or an existing user invited into
    // a merchant org): a ticket cannot be consumed on top of a session, so
    // accept the pending organisation invitations Clerk holds for this
    // email, then leave. Without this an existing user never joined the org.
    if (isSignedIn) {
      if (!ticket || !user) {
        leave();
        return;
      }
      (async () => {
        try {
          const pending = await user.getOrganizationInvitations({ status: 'pending' });
          for (const invitation of pending.data) {
            await invitation.accept();
          }
        } catch {
          // Best effort — the satellite's org picker is the fallback.
        }
        leave();
      })();
      return;
    }

    if (!ticket) {
      setPhase({
        kind: 'error',
        message: t('acceptInvitation.errors.missingTicket'),
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setPhase({ kind: 'completing' });
        const result = await signUp.create({
          strategy: 'ticket',
          ticket,
        });

        if (cancelled) return;

        if (result.status === 'complete' && result.createdSessionId) {
          await setActive({ session: result.createdSessionId });
          setGaIdentityFromSignup({
            firstName: result.firstName,
            lastName: result.lastName,
            email: result.emailAddress,
            userId: result.createdUserId,
          });
          trackGaSignUp({ method: 'ticket' });
          if (redirectUrl) {
            window.location.replace(redirectUrl);
          } else {
            navigate('/');
          }
          return;
        }

        // Clerk needs additional fields (name, etc.) — surface the
        // existing sign-up form. The ticket is still in the URL so the
        // form's `signUp.create` call will reuse it.
        setPhase({ kind: 'fallback' });
      } catch (err: any) {
        if (cancelled) return;
        const code = err?.errors?.[0]?.code as string | undefined;
        const msg = err?.errors?.[0]?.message as string | undefined;
        if (code === 'form_identifier_exists') {
          // The user already has an account: the same ticket signs them
          // in (and accepts the org invitation it carries). Only then
          // does the satellite handshake pick up a session that is a
          // member of the org.
          try {
            const signedIn = await signIn!.create({ strategy: 'ticket', ticket });
            if (cancelled) return;
            if (signedIn.status === 'complete' && signedIn.createdSessionId) {
              await setActive({ session: signedIn.createdSessionId });
            }
          } catch {
            // Ticket already used or expired — the user can sign in normally.
          }
          leave();
          return;
        }
        setPhase({
          kind: 'error',
          message:
            msg ||
            t('acceptInvitation.errors.generic'),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoaded, isLoaded, signInLoaded, isSignedIn, user, ticket, redirectUrl, signUp, signIn, setActive, navigate, t]);

  if (phase.kind === 'fallback') {
    return <CustomSignUp />;
  }

  if (phase.kind === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="flex justify-center">
            <BrandLogo brand={brand} />
          </div>
          <h1 className="text-foreground text-2xl font-semibold">
            {t('acceptInvitation.error.title')}
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed">{phase.message}</p>
          {brandingConfig.supportEmail && (
            <a
              href={`mailto:${brandingConfig.supportEmail}`}
              className="text-foreground hover:text-primary inline-block text-sm font-medium underline underline-offset-4"
            >
              {t('acceptInvitation.error.contactSupport')}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="space-y-4 text-center">
        <div className="border-primary mx-auto h-10 w-10 animate-spin rounded-full border-4 border-t-transparent" />
        <p className="text-muted-foreground text-sm">
          {t('acceptInvitation.completing')}
        </p>
      </div>
    </div>
  );
}
